import { call } from "../api.ts";
import { loadOverview, state, wsBase } from "../state.ts";
import type { Person } from "../types.ts";
import { append, busy, go, h, stopsLine, toast, when } from "../ui.ts";

function filterSelect(label: string, value: string, options: { id: string; text: string }[], all: string, onChange: (v: string) => void) {
  const sel = h("select", { onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value) }, h("option", { value: "" }, all));
  for (const o of options) sel.append(h("option", { value: o.id }, o.text));
  sel.value = value;
  return h("label", { class: "field" }, label, sel);
}

function flags(p: Person) {
  const out = h("span", { class: "flags" });
  if (p.suspended) out.append(h("span", { class: "flag warn" }, "Suspended"));
  if (p.behind) out.append(h("span", { class: "flag warn" }, "Behind"));
  if (p.inactive_7d && !p.suspended) out.append(h("span", { class: "flag" }, "No session in 7 days"));
  return out;
}

function personRow(p: Person) {
  const open = () => go(`person/${p.user_id}`);
  const tracks = h("td", { class: "tracks" });
  if (p.tracks.length === 0) tracks.append(h("span", { class: "muted" }, "No track yet"));
  for (const t of p.tracks) {
    const due = t.assignment?.due_date ? `due ${when(t.assignment.due_date)}` : "";
    tracks.append(
      h(
        "div",
        { class: "track-cell" },
        h("div", { class: "track-name" }, t.title, h("span", { class: "muted" }, ` ${t.modules_done}/${t.modules_total}`), due && h("span", { class: t.assignment?.overdue ? "late" : "muted" }, ` ${due}`)),
        stopsLine(t.stops, t.title),
      ),
    );
  }
  return h(
    "tr",
    { class: "row-link", tabindex: 0, onclick: open, onkeydown: (e: Event) => (e as KeyboardEvent).key === "Enter" && open() },
    h("td", null, h("div", { class: "who" }, p.name ?? p.email), h("div", { class: "muted small" }, p.groups.map((g) => g.path).join(", ") || p.email), flags(p)),
    tracks,
    h("td", { class: "num" }, p.avg_score ?? "–"),
    h("td", { class: "num" }, p.sessions_week ? `${p.minutes_week} min` : "–"),
    h("td", null, when(p.last_active)),
  );
}

export async function renderOverview(root: HTMLElement) {
  const o = await loadOverview();
  const t = o.totals;
  const rerun = () => {
    state.overview = null;
    void renderOverview(root).catch((e) => toast(String(e), "error"));
  };
  const exportBtn = h("button", { type: "button" }, "Download CSV") as HTMLButtonElement;
  exportBtn.addEventListener("click", () =>
    busy(exportBtn, async () => {
      const q = new URLSearchParams();
      if (state.filters.group) q.set("group", state.filters.group);
      if (state.filters.track) q.set("track", state.filters.track);
      const r = await call("GET", `${wsBase()}/export.csv${q.size ? `?${q}` : ""}`);
      const href = URL.createObjectURL(await r.blob());
      const a = h("a", { href, download: `${o.workspace.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-training.csv` });
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    }),
  );

  const scope = o.me.admin ? "Everyone in the company" : `Your groups: ${o.me.led_groups.map((g) => g.path).join(", ")}`;
  append(
    root,
    h(
      "header",
      { class: "page-head" },
      h("div", null, h("h1", null, "Training this week"), h("p", { class: "muted" }, scope)),
    ),
    h(
      "section",
      { class: "summary", "aria-label": "This week" },
      h("p", { class: "lede" }, `${t.active_this_week} of ${t.people} people trained this week, ${t.hours_this_week} hours in all.`),
      h(
        "dl",
        { class: "figures" },
        h("div", null, h("dt", null, "Modules finished"), h("dd", null, t.modules_done)),
        h("div", null, h("dt", null, "Average checkpoint score"), h("dd", null, t.avg_score ?? "–")),
        h("div", { class: t.behind ? "alert" : "" }, h("dt", null, "Behind on a due date"), h("dd", null, t.behind)),
        h("div", { class: t.inactive_7d ? "alert" : "" }, h("dt", null, "No session in 7 days"), h("dd", null, t.inactive_7d)),
      ),
    ),
    h(
      "div",
      { class: "toolbar" },
      filterSelect("Group", state.filters.group, o.groups.map((g) => ({ id: g.id, text: g.path })), "All groups", (v) => {
        state.filters.group = v;
        rerun();
      }),
      filterSelect("Track", state.filters.track, o.tracks.map((x) => ({ id: x.id, text: x.title })), "All tracks", (v) => {
        state.filters.track = v;
        rerun();
      }),
      h("span", { class: "grow" }),
      exportBtn,
    ),
  );
  if (o.people.length === 0) {
    root.append(
      h(
        "p",
        { class: "empty" },
        o.me.admin ? "Nobody matches. Invite people on the People page, then give them a track on the Tracks page." : "Nobody is in your groups yet. Ask your admin to add them.",
      ),
    );
    return;
  }
  const body = h("tbody");
  for (const p of o.people) body.append(personRow(p));
  root.append(
    h(
      "div",
      { class: "table-wrap" },
      h(
        "table",
        { class: "people" },
        h("thead", null, h("tr", null, h("th", null, "Person"), h("th", null, "Tracks and modules"), h("th", { class: "num" }, "Score"), h("th", { class: "num" }, "This week"), h("th", null, "Last session"))),
        body,
      ),
    ),
  );
}
